
import React, { useRef, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthPaymentBlocker } from '@/components/courier/AuthPaymentBlocker';

interface AuthPaymentProtectedRouteProps {
  children: JSX.Element;
  isAllowed: boolean;
  redirectPath?: string;
  bypassAuthCheck?: boolean;
}

export const AuthPaymentProtectedRoute: React.FC<AuthPaymentProtectedRouteProps> = ({ 
  children, 
  isAllowed,
  redirectPath = "/login",
  bypassAuthCheck = false
}) => {
  const { user, isLoading, isAdmin } = useAuth();
  const location = useLocation();
  // Используем реф для отслеживания состояния редиректа
  const redirected = useRef<boolean>(false);
  const redirectAttemptCount = useRef<number>(0);
  
  // Сбрасываем счетчик редиректов при смене пути
  useEffect(() => {
    redirected.current = false;
    redirectAttemptCount.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  try {
    // Отладочная информация
    console.log(`AuthPaymentProtectedRoute: путь=${location.pathname}, разрешен=${isAllowed}, счетчик=${redirectAttemptCount.current}, bypassAuthCheck=${bypassAuthCheck}`);
    
    // Защита от циклических редиректов - если уже было 2 попытки, показываем текущий компонент
    if (redirectAttemptCount.current > 2) {
      console.warn(`Предотвращен циклический редирект: ${location.pathname}`);
      return children;
    }
    
    // Специальное правило: администраторы всегда имеют доступ к маршрутам
    if (isAdmin && redirectPath !== "/login") {
      return children;
    }
    
    // Если маршрут не авторизован, редиректим на логин
    if (!isAllowed && !redirected.current) {
      redirected.current = true;
      redirectAttemptCount.current += 1;
      
      // Задержка перед редиректом для стабилизации состояния
      // Возвращаем временную заглушку на время задержки
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <div className="ml-3">Проверка авторизации...</div>
        </div>
      );
    }
    
    // Если маршрут должен обойти проверку авторизационных платежей
    if (bypassAuthCheck) {
      console.log('AuthPaymentProtectedRoute: Обход проверки авторизационных платежей');
      return <AuthPaymentBlocker bypassCheck={true}>{children}</AuthPaymentBlocker>;
    }
    
    // Проверяем авторизационные платежи только для авторизованных пользователей
    return <AuthPaymentBlocker>{children}</AuthPaymentBlocker>;
    
  } catch (error) {
    console.error('Ошибка в защищённом маршруте:', error);
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
        <div className="mb-4 text-red-500">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold mb-2">Произошла ошибка</h2>
        <p className="mb-4 text-muted-foreground">При загрузке страницы возникла проблема</p>
        <button 
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          onClick={() => window.location.reload()}
        >
          Обновить страницу
        </button>
      </div>
    );
  }
};
